import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import { useClientApi } from '@hero/api-hooks';
import { CircleCheckIcon, LocationIcon, PencilIcon } from '@hero/web-icons';
import { Input } from '../../../ui/Input/Input';
import { Switch } from '../../../ui/Switch/Switch';
import { EntityLink } from './widgets/EntityLink';
import type { HeroEntityKind } from './widgets/openEntityDrawer';
import { normalizeEntityId } from './widgets/normalizeEntityId';

/**
 * Building blocks every confirmation card body is assembled from.
 *
 * Three rules live here so no card has to remember them:
 *
 * 1. **Labels are sentence case, never SHOUTED.** Card chrome uses
 *    {@link humaniseKey} + normal casing — no `uppercase` utilities.
 * 2. **Base fields are editable in place.** A staged name, phone, or
 *    note is a text input wired straight to the staged payload, so
 *    the user corrects it here instead of retyping the request.
 * 3. **An entity is a name that opens its drawer, never an id.** Use
 *    {@link EntityRefField} / {@link ClientRefField}; a raw ObjectId
 *    must never reach the screen.
 */

/**
 * Every card row is a label above one full-width control of the same
 * height, so a stack of them reads as one form instead of four widgets
 * that happen to sit together. Anything mounted inside a row (a shared
 * selector, say) gets {@link cardControlClass} so it matches.
 */
export const cardControlClass =
  'h-9 min-h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm';

/** Label + control wrapper. The one place row rhythm is decided. */
export const CardFieldShell = ({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  /** Shown under the control — why it's blocking, what it will do. */
  hint?: ReactNode;
  children: ReactNode;
}) => (
  <div className='flex flex-col gap-1'>
    <span className='text-[11px] font-medium text-slate-500'>
      {label}
      {required ? <span className='ms-0.5 text-red-500'>*</span> : null}
    </span>
    {children}
    {hint}
  </div>
);

/** Read-only label + value pair. */
export const CardField = ({
  label,
  value,
  dir,
}: {
  label: string;
  value: ReactNode;
  dir?: 'ltr' | 'rtl';
}) => (
  <CardFieldShell label={label}>
    <span className='break-words text-sm text-slate-800' dir={dir}>
      {value}
    </span>
  </CardFieldShell>
);

/**
 * Editable single-line field. Writes on every keystroke so Confirm
 * always sends what is on screen — there is no separate "apply"
 * step to forget.
 */
export const CardTextField = ({
  label,
  value,
  placeholder,
  dir,
  disabled,
  required,
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  dir?: 'ltr' | 'rtl';
  disabled?: boolean;
  required?: boolean;
  onChange: (next: string) => void;
}) => (
  <CardFieldShell label={label} required={required}>
    <Input
      value={value}
      placeholder={placeholder}
      dir={dir}
      disabled={disabled}
      onChange={e => onChange(e.target.value)}
      className={cardControlClass}
    />
  </CardFieldShell>
);

/** Editable numeric field. Empty input clears the value. */
export const CardNumberField = ({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: number | undefined;
  disabled?: boolean;
  onChange: (next: number | undefined) => void;
}) => (
  <CardFieldShell label={label}>
    <Input
      type='number'
      value={value ?? ''}
      disabled={disabled}
      onChange={e => {
        const next = e.target.value.trim();
        onChange(next === '' ? undefined : Number(next));
      }}
      className={cardControlClass}
    />
  </CardFieldShell>
);

/** Editable boolean — a switch, not the word "false". */
export const CardSwitchField = ({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) => (
  <div className='flex h-9 items-center justify-between gap-2'>
    <span className='text-[11px] font-medium text-slate-500'>{label}</span>
    <Switch checked={value} disabled={disabled} onCheckedChange={onChange} />
  </div>
);

/**
 * A reference to another entity: its name as a chip that opens the
 * entity's drawer. `fallbackLabel` covers records whose display name
 * wasn't staged — a kind label ("Order") still beats an ObjectId.
 */
export const EntityRefField = ({
  label,
  kind,
  entityId,
  fallbackLabel,
}: {
  label: string;
  kind: HeroEntityKind;
  entityId: string;
  fallbackLabel: string;
}) => {
  const id = normalizeEntityId(entityId);
  return (
    <CardFieldShell label={label}>
      {/* Control-height row so the chip lines up with the inputs above
          and below it instead of floating at its own size. */}
      <span className='flex h-9 items-center'>
        {id ? (
          <EntityLink kind={kind} entityId={id} label={fallbackLabel} variant='inline' />
        ) : (
          <span className='text-sm text-slate-800'>{fallbackLabel}</span>
        )}
      </span>
    </CardFieldShell>
  );
};

/**
 * Client reference that resolves its own name.
 *
 * The staged payload only carries `clientName` when the agent had to
 * *look the client up*; when the model already knew the id there is
 * no name to show. Fetching here means the card shows "Berain" either
 * way, and the id stays where it belongs — in the drawer it opens.
 */
export const ClientRefField = ({
  label,
  clientId,
  fallbackName,
}: {
  label: string;
  clientId: string;
  fallbackName?: string;
}) => {
  const { t } = useTranslation('m-agent');
  const id = normalizeEntityId(clientId);
  const { data: client } = useClientApi(id || undefined);
  const name =
    client?.name?.trim() ||
    fallbackName?.trim() ||
    t('confirmation.fields.client_fallback', { defaultValue: 'Client' });

  return (
    <EntityRefField label={label} kind='client' entityId={clientId} fallbackLabel={name} />
  );
};

/**
 * The location row: a human place label plus an accuracy hint,
 * tappable to open the picker. Coordinates never render — if all we
 * have is a pin with no label, we say so and let the user fix it.
 */
export const CardLocationField = ({
  label,
  placeLabel,
  addressLabel,
  isAccurate,
  hasPin,
  required,
  onEdit,
}: {
  label: string;
  placeLabel?: string;
  addressLabel?: string;
  isAccurate?: boolean;
  hasPin: boolean;
  required?: boolean;
  onEdit: () => void;
}) => {
  const { t } = useTranslation('m-agent');

  return (
    <CardFieldShell label={label} required={required}>
      <button
        type='button'
        onClick={onEdit}
        className={cn(
          cardControlClass,
          'flex min-h-9 items-center gap-2 py-1.5 text-start transition-colors',
          hasPin
            ? 'border-slate-200 hover:border-primary-300 hover:bg-primary-50/30'
            : 'border-dashed border-amber-300 bg-amber-50/40 hover:bg-amber-50',
        )}>
        <LocationIcon
          className={cn('size-4 shrink-0', hasPin ? 'text-primary-600' : 'text-amber-600')}
        />
        <span className='flex min-w-0 flex-1 flex-col'>
          <span className='truncate text-sm font-medium text-slate-800'>
            {hasPin
              ? placeLabel?.trim() ||
                addressLabel?.trim() ||
                t('confirmation.location.dropped_pin', { defaultValue: 'Pinned on the map' })
              : t('confirmation.location.pick_cta', { defaultValue: 'Choose a location' })}
          </span>
          {hasPin && addressLabel?.trim() && addressLabel.trim() !== placeLabel?.trim() ? (
            <span className='truncate text-[11px] text-slate-500'>{addressLabel.trim()}</span>
          ) : null}
        </span>
        {hasPin ? (
          isAccurate ? (
            <span className='inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700'>
              <CircleCheckIcon className='size-3' />
              {t('confirmation.location.exact', { defaultValue: 'Exact pin' })}
            </span>
          ) : (
            <span className='shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800'>
              {t('confirmation.location.approximate', { defaultValue: 'Approximate' })}
            </span>
          )
        ) : null}
        <PencilIcon className='size-3.5 shrink-0 text-slate-400' />
      </button>
    </CardFieldShell>
  );
};

/** `client_name` / `clientName` → "Client name". Never upper-cased. */
export const humaniseKey = (key: string): string =>
  key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, c => c.toUpperCase());
